import { useConfirm } from '../components/ConfirmProvider';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { participantApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  LogOut, FileText, TrendingUp, Calendar, MessageSquare,
  Users, Heart, Compass, Smile, HandHelping, Send,
  Plus, ChevronRight, Clock, Star, Activity, TrendingDown, Minus, Sparkles
} from 'lucide-react';
import { LogoHome } from '../components/LogoHome';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_e3b3c70d-c72e-49dc-b41a-da6affdc6e46/artifacts/4w4rfom9_IMG_0777.PNG";

const CATEGORY_CONFIG = {
  community_independence: { label: 'Community Independence', icon: Compass, color: 'text-blue-500', bg: 'bg-blue-500/10' },
  social_participation: { label: 'Social Participation', icon: Users, color: 'text-purple-500', bg: 'bg-purple-500/10' },
  daily_living: { label: 'Daily Living Skills', icon: HandHelping, color: 'text-amber-500', bg: 'bg-amber-500/10' },
  health_wellbeing: { label: 'Health & Wellbeing', icon: Heart, color: 'text-rose-500', bg: 'bg-rose-500/10' },
};

export default function ParticipantDashboard() {
  const confirm = useConfirm();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [progressSummary, setProgressSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState(null);
  const [commentText, setCommentText] = useState('');
  const [sendingComment, setSendingComment] = useState(false);
  // Progress form
  const [progressCategory, setProgressCategory] = useState('community_independence');
  const [progressScore, setProgressScore] = useState([5]);
  const [progressNotes, setProgressNotes] = useState('');
  const [progressGoals, setProgressGoals] = useState('');
  const [savingProgress, setSavingProgress] = useState(false);
  // Goals
  const [goals, setGoals] = useState([]);
  const [goalIdeas, setGoalIdeas] = useState([]);
  const [newGoalTitle, setNewGoalTitle] = useState('');
  const [newGoalDesc, setNewGoalDesc] = useState('');
  const [newGoalCategory, setNewGoalCategory] = useState('general');
  const [newGoalTarget, setNewGoalTarget] = useState('');
  const [newGoalSteps, setNewGoalSteps] = useState('');
  const [savingGoal, setSavingGoal] = useState(false);
  // Behaviours
  const [behaviourSummary, setBehaviourSummary] = useState(null);
  const [behaviourComparison, setBehaviourComparison] = useState(null);
  const [behaviourRecords, setBehaviourRecords] = useState([]);
  const [behaviourLoading, setBehaviourLoading] = useState(false);
  // Celebrations
  const [celebrations, setCelebrations] = useState([]);
  const [celebrateOpen, setCelebrateOpen] = useState(false);
  const [celebrateCategory, setCelebrateCategory] = useState(null);
  const [celebrateMessage, setCelebrateMessage] = useState('');
  const [sendingCelebration, setSendingCelebration] = useState(false);
  // Shift request
  const [shiftDate, setShiftDate] = useState('');
  const [shiftTime, setShiftTime] = useState('');
  const [shiftNotes, setShiftNotes] = useState('');
  const [shiftType, setShiftType] = useState('new');
  const [savingShift, setSavingShift] = useState(false);
  // Meeting request
  const [meetingWith, setMeetingWith] = useState('worker');
  const [meetingDate, setMeetingDate] = useState('');
  const [meetingReason, setMeetingReason] = useState('');
  const [savingMeeting, setSavingMeeting] = useState(false);

  useEffect(() => { loadDashboard(); loadBehaviours(); }, []);

  const loadDashboard = async () => {
    try {
      const [dashRes, progRes, goalsRes, ideasRes] = await Promise.all([
        participantApi.getDashboard(),
        participantApi.getProgressSummary(),
        participantApi.getGoals().catch(() => ({ data: [] })),
        participantApi.getGoalIdeas().catch(() => ({ data: [] })),
      ]);
      setDashboard(dashRes.data);
      setProgressSummary(progRes.data);
      setGoals(goalsRes.data);
      setGoalIdeas(ideasRes.data);
    } catch (err) {
      if (err.response?.status === 404) {
        toast.error('Your account is not yet linked to a participant profile. Please contact your Organisation.');
      } else {
        toast.error('Failed to load dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadBehaviours = async () => {
    setBehaviourLoading(true);
    try {
      const [summaryRes, compRes, recordsRes, celebsRes] = await Promise.all([
        participantApi.getBehaviourSummary(90).catch(() => ({ data: null })),
        participantApi.getBehaviourComparison().catch(() => ({ data: null })),
        participantApi.getBehaviourRecords(60).catch(() => ({ data: [] })),
        participantApi.getCelebrations().catch(() => ({ data: [] })),
      ]);
      setBehaviourSummary(summaryRes.data);
      setBehaviourComparison(compRes.data);
      setBehaviourRecords(recordsRes.data || []);
      setCelebrations(celebsRes.data || []);
    } catch {
      // non-fatal
    } finally {
      setBehaviourLoading(false);
    }
  };

  const openCelebrate = (positive) => {
    setCelebrateCategory(positive);
    setCelebrateMessage('');
    setCelebrateOpen(true);
  };

  const handleSendCelebration = async () => {
    if (!celebrateCategory) return;
    setSendingCelebration(true);
    try {
      const res = await participantApi.celebrate({
        category: celebrateCategory.category,
        category_name: celebrateCategory.name,
        message: celebrateMessage || null,
      });
      const notified = res.data?.workers_notified || 0;
      toast.success(notified > 0
        ? `🎉 Celebration sent to ${notified} support worker${notified !== 1 ? 's' : ''}!`
        : '🎉 Celebration recorded — your team will be notified once a session is logged.');
      setCelebrateOpen(false);
      setCelebrateCategory(null);
      setCelebrateMessage('');
      loadBehaviours();
    } catch (err) {
      toast.error('Failed to send celebration');
    } finally {
      setSendingCelebration(false);
    }
  };

  const handleAddComment = async () => {
    if (!commentText.trim() || !selectedReport) return;
    setSendingComment(true);
    try {
      await participantApi.addComment(selectedReport.id, commentText.trim());
      setCommentText('');
      toast.success('Comment added');
      // Reload report with comments
      const res = await participantApi.getReport(selectedReport.id);
      setSelectedReport(res.data.report);
      setSelectedReport(prev => ({ ...prev, _comments: res.data.comments }));
    } catch (err) {
      toast.error('Failed to add comment');
    } finally {
      setSendingComment(false);
    }
  };

  const handleAddProgress = async () => {
    setSavingProgress(true);
    try {
      await participantApi.addProgress({
        category: progressCategory,
        score: progressScore[0],
        notes: progressNotes || null,
        goals: progressGoals ? progressGoals.split('\n').filter(g => g.trim()) : [],
      });
      toast.success('Progress logged!');
      setProgressNotes('');
      setProgressGoals('');
      setProgressScore([5]);
      loadDashboard();
    } catch (err) {
      toast.error('Failed to save progress');
    } finally {
      setSavingProgress(false);
    }
  };

  const handleShiftRequest = async () => {
    if (!shiftDate) { toast.error('Please select a date'); return; }
    setSavingShift(true);
    try {
      await participantApi.createShiftRequest({
        request_type: shiftType,
        preferred_date: shiftDate,
        preferred_time: shiftTime || null,
        notes: shiftNotes || null,
      });
      toast.success(shiftType === 'new' ? 'Shift requested!' : 'Cancellation submitted');
      setShiftDate(''); setShiftTime(''); setShiftNotes('');
      loadDashboard();
    } catch (err) {
      toast.error('Failed to submit request');
    } finally {
      setSavingShift(false);
    }
  };

  const handleMeetingRequest = async () => {
    setSavingMeeting(true);
    try {
      await participantApi.createMeetingRequest({
        requested_with: meetingWith,
        preferred_date: meetingDate || null,
        reason: meetingReason || null,
      });
      toast.success('Meeting request sent!');
      setMeetingDate(''); setMeetingReason('');
      loadDashboard();
    } catch (err) {
      toast.error('Failed to request meeting');
    } finally {
      setSavingMeeting(false);
    }
  };

  const handleCreateGoal = async () => {
    if (!newGoalTitle.trim()) { toast.error('Please enter a goal title'); return; }
    setSavingGoal(true);
    try {
      await participantApi.createGoal({
        title: newGoalTitle.trim(),
        description: newGoalDesc || null,
        category: newGoalCategory,
        target_date: newGoalTarget || null,
        steps: newGoalSteps ? newGoalSteps.split('\n').filter(s => s.trim()) : [],
      });
      toast.success('Goal created!');
      setNewGoalTitle(''); setNewGoalDesc(''); setNewGoalTarget(''); setNewGoalSteps('');
      loadDashboard();
    } catch (err) {
      toast.error('Failed to create goal');
    } finally {
      setSavingGoal(false);
    }
  };

  const handleUpdateGoal = async (goalId, updates) => {
    try {
      await participantApi.updateGoal(goalId, updates);
      setGoals(prev => prev.map(g => g.id === goalId ? { ...g, ...updates } : g));
      toast.success('Goal updated');
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const handleDeleteGoal = async (goalId) => {
    if (!(await confirm({ title: 'Delete this goal?', confirmText: 'Delete', destructive: true }))) return;
    try {
      await participantApi.deleteGoal(goalId);
      setGoals(prev => prev.filter(g => g.id !== goalId));
      toast.success('Goal removed');
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleAddIdeaAsGoal = (idea, category) => {
    setNewGoalTitle(idea);
    setNewGoalCategory(category);
  };

  const handleLogout = () => { logout(); navigate('/login'); };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  // Build chart data from all categories
  const chartData = {};
  progressSummary.forEach(cat => {
    cat.trend?.forEach(t => {
      if (!chartData[t.date]) chartData[t.date] = { date: t.date };
      chartData[t.date][cat.category] = t.score;
    });
  });
  const trendData = Object.values(chartData).sort((a, b) => a.date.localeCompare(b.date));

  return (
    <div className="min-h-screen bg-slate-50" data-testid="participant-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <LogoHome className="h-10 w-auto" />
            <div className="hidden sm:block">
              <h1 className="font-semibold text-slate-900 text-sm">Support Portal</h1>
              <p className="text-xs text-slate-500">{dashboard?.client?.name || user?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-600 hidden sm:block">{user?.name}</span>
            <Button variant="outline" size="sm" onClick={handleLogout}>
              <LogOut className="h-4 w-4 mr-1" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Welcome */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-slate-900">
              Welcome, {dashboard?.client?.name || user?.name}
            </h2>
            <p className="text-slate-500 text-sm mt-0.5">Track your support progress and stay connected with your team</p>
          </div>
          <div className="flex gap-2">
            <Badge variant="outline" className="text-teal-600 border-teal-300">
              {dashboard?.stats?.total_reports || 0} Reports
            </Badge>
            {dashboard?.stats?.avg_compliance > 0 && (
              <Badge variant="outline" className="text-emerald-600 border-emerald-300">
                {dashboard?.stats?.avg_compliance}% Compliance
              </Badge>
            )}
          </div>
        </div>

        {/* Progress Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {progressSummary.map(cat => {
            const config = CATEGORY_CONFIG[cat.category];
            const Icon = config?.icon || Star;
            return (
              <Card key={cat.category} className="border-slate-200" data-testid={`progress-card-${cat.category}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`p-1.5 rounded-lg ${config?.bg}`}>
                      <Icon className={`h-4 w-4 ${config?.color}`} />
                    </div>
                    <p className="text-xs font-medium text-slate-600 truncate">{cat.name}</p>
                  </div>
                  <p className="text-2xl font-bold text-slate-900">{cat.latest_score || '—'}<span className="text-sm text-slate-400 font-normal">/10</span></p>
                  <div className="flex items-center gap-1 mt-0.5">
                    <p className="text-xs text-slate-400">Avg: {cat.average || 0}/10</p>
                    {cat.latest_source === 'ai' && (
                      <Badge className="bg-purple-100 text-purple-700 text-[9px] px-1 py-0">AI</Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <Tabs defaultValue="progress" className="space-y-4">
          <TabsList className="bg-white border border-slate-200 flex-wrap">
            <TabsTrigger value="progress" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white">Progress</TabsTrigger>
            <TabsTrigger value="goals" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white">My Goals</TabsTrigger>
            <TabsTrigger value="behaviour" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white" data-testid="tab-behaviour">Behaviour</TabsTrigger>
            <TabsTrigger value="reports" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white">Reports</TabsTrigger>
            <TabsTrigger value="shifts" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white">Shifts</TabsTrigger>
            <TabsTrigger value="connect" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white">Connect</TabsTrigger>
          </TabsList>

          {/* =================== PROGRESS TAB =================== */}
          <TabsContent value="progress" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Progress Chart */}
              <Card className="lg:col-span-2 border-slate-200">
                <CardHeader><CardTitle className="text-base">Progress Over Time</CardTitle></CardHeader>
                <CardContent>
                  {trendData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={280}>
                      <LineChart data={trendData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                        <XAxis dataKey="date" stroke="#94A3B8" tick={{ fontSize: 11 }} tickFormatter={v => v.slice(5)} />
                        <YAxis stroke="#94A3B8" tick={{ fontSize: 11 }} domain={[0, 10]} />
                        <Tooltip />
                        <Legend />
                        <Line type="monotone" dataKey="community_independence" stroke="#3B82F6" strokeWidth={2} name="Community" dot={{ r: 3 }} />
                        <Line type="monotone" dataKey="social_participation" stroke="#8B5CF6" strokeWidth={2} name="Social" dot={{ r: 3 }} />
                        <Line type="monotone" dataKey="daily_living" stroke="#F59E0B" strokeWidth={2} name="Daily Living" dot={{ r: 3 }} />
                        <Line type="monotone" dataKey="health_wellbeing" stroke="#EF4444" strokeWidth={2} name="Health" dot={{ r: 3 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[280px] flex flex-col items-center justify-center text-slate-400">
                      <TrendingUp className="h-10 w-10 mb-3 text-slate-300" />
                      <p>No progress data yet</p>
                      <p className="text-sm">Log your first entry to start tracking!</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Log Progress Form */}
              <Card className="border-slate-200">
                <CardHeader><CardTitle className="text-base flex items-center gap-2"><Plus className="h-4 w-4" /> Log Progress</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label className="text-sm">Category</Label>
                    <Select value={progressCategory} onValueChange={setProgressCategory}>
                      <SelectTrigger className="mt-1" data-testid="progress-category-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(CATEGORY_CONFIG).map(([key, val]) => (
                          <SelectItem key={key} value={key}>{val.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm">Score: {progressScore[0]}/10</Label>
                    <Slider value={progressScore} onValueChange={setProgressScore} min={1} max={10} step={1} className="mt-2" data-testid="progress-score-slider" />
                  </div>
                  <div>
                    <Label className="text-sm">Notes (optional)</Label>
                    <Textarea value={progressNotes} onChange={(e) => setProgressNotes(e.target.value)} placeholder="How are you going with this area?" rows={2} className="mt-1" data-testid="progress-notes" />
                  </div>
                  <div>
                    <Label className="text-sm">Goals (one per line)</Label>
                    <Textarea value={progressGoals} onChange={(e) => setProgressGoals(e.target.value)} placeholder="Visit the shops independently&#10;Catch public transport" rows={2} className="mt-1" data-testid="progress-goals" />
                  </div>
                  <Button onClick={handleAddProgress} disabled={savingProgress} className="w-full bg-teal-600 hover:bg-teal-700" data-testid="save-progress-btn">
                    <TrendingUp className="h-4 w-4 mr-2" /> {savingProgress ? 'Saving...' : 'Log Progress'}
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>


          {/* =================== GOALS TAB =================== */}
          <TabsContent value="goals" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Goal Creation Form */}
              <Card className="border-slate-200 lg:col-span-1">
                <CardHeader><CardTitle className="text-base flex items-center gap-2"><Plus className="h-4 w-4" /> Set a New Goal</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <Label className="text-sm">Goal Title</Label>
                    <Input value={newGoalTitle} onChange={(e) => setNewGoalTitle(e.target.value)} placeholder="What do you want to achieve?" className="mt-1" data-testid="goal-title-input" />
                  </div>
                  <div>
                    <Label className="text-sm">Category</Label>
                    <Select value={newGoalCategory} onValueChange={setNewGoalCategory}>
                      <SelectTrigger className="mt-1" data-testid="goal-category-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="community_independence">Community Independence</SelectItem>
                        <SelectItem value="social_participation">Social Participation</SelectItem>
                        <SelectItem value="daily_living">Daily Living Skills</SelectItem>
                        <SelectItem value="health_wellbeing">Health & Wellbeing</SelectItem>
                        <SelectItem value="general">General</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm">Description (optional)</Label>
                    <Textarea value={newGoalDesc} onChange={(e) => setNewGoalDesc(e.target.value)} placeholder="More details about this goal..." rows={2} className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-sm">Target Date (optional)</Label>
                    <Input type="date" value={newGoalTarget} onChange={(e) => setNewGoalTarget(e.target.value)} className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-sm">Steps (one per line, optional)</Label>
                    <Textarea value={newGoalSteps} onChange={(e) => setNewGoalSteps(e.target.value)} placeholder="Step 1&#10;Step 2&#10;Step 3" rows={3} className="mt-1" data-testid="goal-steps-input" />
                  </div>
                  <Button onClick={handleCreateGoal} disabled={savingGoal} className="w-full bg-teal-600 hover:bg-teal-700" data-testid="create-goal-btn">
                    <Star className="h-4 w-4 mr-2" /> {savingGoal ? 'Creating...' : 'Create Goal'}
                  </Button>
                </CardContent>
              </Card>

              {/* Active Goals */}
              <div className="lg:col-span-2 space-y-3">
                <h3 className="text-lg font-semibold text-slate-900">My Goals ({goals.length})</h3>
                {goals.length === 0 ? (
                  <Card className="border-slate-200">
                    <CardContent className="py-12 text-center">
                      <Star className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-400">No goals set yet</p>
                      <p className="text-sm text-slate-500">Create your first goal or pick one from the ideas below!</p>
                    </CardContent>
                  </Card>
                ) : (
                  goals.map(goal => {
                    const catConfig = CATEGORY_CONFIG[goal.category];
                    const Icon = catConfig?.icon || Star;
                    return (
                      <Card key={goal.id} className="border-slate-200" data-testid={`goal-card-${goal.id}`}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex items-start gap-3 flex-1">
                              <div className={`p-2 rounded-lg mt-0.5 ${catConfig?.bg || 'bg-slate-100'}`}>
                                <Icon className={`h-4 w-4 ${catConfig?.color || 'text-slate-500'}`} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <h4 className="font-medium text-slate-900">{goal.title}</h4>
                                  <Badge variant="outline" className={`text-[10px] capitalize ${goal.status === 'completed' ? 'text-emerald-600 border-emerald-300' : goal.status === 'in_progress' ? 'text-blue-600 border-blue-300' : 'text-slate-500 border-slate-300'}`}>
                                    {goal.status?.replace('_', ' ')}
                                  </Badge>
                                </div>
                                {goal.description && <p className="text-sm text-slate-500 mt-0.5">{goal.description}</p>}
                                {goal.target_date && <p className="text-xs text-slate-400 mt-1">Target: {goal.target_date}</p>}
                                {goal.steps?.length > 0 && (
                                  <div className="mt-2 space-y-1">
                                    {goal.steps.map((step, i) => (
                                      <div key={i} className="flex items-center gap-2 text-xs text-slate-600">
                                        <div className="h-1.5 w-1.5 rounded-full bg-teal-400" />
                                        {step}
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {/* Progress bar */}
                                <div className="mt-3 flex items-center gap-2">
                                  <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                                    <div className="h-full bg-teal-500 rounded-full transition-all" style={{ width: `${goal.progress || 0}%` }} />
                                  </div>
                                  <span className="text-xs text-slate-500 w-8">{goal.progress || 0}%</span>
                                </div>
                              </div>
                            </div>
                          </div>
                          {/* Actions */}
                          <div className="flex items-center gap-2 mt-3 ml-11">
                            {goal.status !== 'completed' && (
                              <>
                                <Button size="sm" variant="outline" className="h-7 text-xs"
                                  onClick={() => handleUpdateGoal(goal.id, { progress: Math.min(100, (goal.progress || 0) + 25) })}>
                                  +25%
                                </Button>
                                <Button size="sm" variant="outline" className="h-7 text-xs text-emerald-600 border-emerald-300"
                                  onClick={() => handleUpdateGoal(goal.id, { status: 'completed', progress: 100 })} data-testid={`complete-goal-${goal.id}`}>
                                  Complete
                                </Button>
                                <Button size="sm" variant="outline" className="h-7 text-xs"
                                  onClick={() => handleUpdateGoal(goal.id, { status: goal.status === 'in_progress' ? 'active' : 'in_progress' })}>
                                  {goal.status === 'in_progress' ? 'Pause' : 'Start'}
                                </Button>
                              </>
                            )}
                            <Button size="sm" variant="ghost" className="h-7 text-xs text-red-400 hover:text-red-600 ml-auto"
                              onClick={() => handleDeleteGoal(goal.id)}>
                              Remove
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })
                )}
              </div>
            </div>

            {/* Ideas & Inspiration */}
            <Card className="border-slate-200 mt-4">
              <CardHeader>
                <CardTitle className="text-base">Ideas & Inspiration</CardTitle>
                <p className="text-sm text-slate-500">Pick any idea below to add it as a goal</p>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {goalIdeas.map(group => {
                    const catConfig = CATEGORY_CONFIG[group.category];
                    const Icon = catConfig?.icon || Star;
                    return (
                      <div key={group.category} className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Icon className={`h-4 w-4 ${catConfig?.color || 'text-slate-500'}`} />
                          <span className="text-sm font-medium text-slate-700">{catConfig?.label || group.category}</span>
                        </div>
                        <div className="space-y-1 pl-6">
                          {group.ideas.map((idea, i) => (
                            <button
                              key={i}
                              onClick={() => handleAddIdeaAsGoal(idea, group.category)}
                              className="w-full text-left text-sm text-slate-600 hover:text-teal-600 hover:bg-teal-50 px-2 py-1.5 rounded transition-colors"
                              data-testid={`goal-idea-${group.category}-${i}`}
                            >
                              {idea}
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>


          {/* =================== BEHAVIOUR TAB =================== */}
          <TabsContent value="behaviour" className="space-y-4" data-testid="behaviour-tab-content">
            {behaviourLoading ? (
              <div className="flex items-center justify-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
              </div>
            ) : (!behaviourSummary || behaviourSummary.total_records === 0) ? (
              <Card className="border-slate-200">
                <CardContent className="py-12 text-center">
                  <Activity className="h-10 w-10 mx-auto mb-3 text-slate-300" />
                  <p className="text-slate-400">No behaviour insights yet</p>
                  <p className="text-sm text-slate-500 mt-1">
                    Your team will analyse shift reports to track patterns, triggers, and positive moments.
                    Check back as more reports are reviewed.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Period stats */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  <Card className="border-slate-200">
                    <CardContent className="p-4">
                      <p className="text-xs text-slate-500 mb-1">Observations (90d)</p>
                      <p className="text-2xl font-bold text-slate-900">{behaviourSummary.total_records}</p>
                    </CardContent>
                  </Card>
                  <Card className="border-slate-200">
                    <CardContent className="p-4">
                      <p className="text-xs text-slate-500 mb-1">Positive moments</p>
                      <p className="text-2xl font-bold text-emerald-600">
                        {behaviourSummary.positives.reduce((s, p) => s + p.count, 0)}
                      </p>
                    </CardContent>
                  </Card>
                  <Card className="border-slate-200">
                    <CardContent className="p-4">
                      <p className="text-xs text-slate-500 mb-1">Challenges</p>
                      <p className="text-2xl font-bold text-amber-600">
                        {behaviourSummary.challenges.reduce((s, c) => s + c.count, 0)}
                      </p>
                    </CardContent>
                  </Card>
                  <Card className="border-slate-200">
                    <CardContent className="p-4">
                      <p className="text-xs text-slate-500 mb-1">This vs last month</p>
                      <p className={`text-2xl font-bold ${(behaviourComparison?.total_change || 0) < 0 ? 'text-emerald-600' : (behaviourComparison?.total_change || 0) > 0 ? 'text-amber-600' : 'text-slate-700'}`}>
                        {(behaviourComparison?.total_change || 0) > 0 ? '+' : ''}{behaviourComparison?.total_change || 0}
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {/* Celebrations counter */}
                {celebrations.length > 0 && (
                  <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 w-fit" data-testid="celebrations-counter">
                    <Sparkles className="h-4 w-4" />
                    You've sent <strong>{celebrations.length}</strong> celebration{celebrations.length !== 1 ? 's' : ''} to your team 💚
                  </div>
                )}

                {/* Positive behaviours */}
                {behaviourSummary.positives.length > 0 && (
                  <Card className="border-emerald-200 bg-emerald-50/40">
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2 text-emerald-800">
                        <Sparkles className="h-4 w-4" /> Wins & Positive Moments
                      </CardTitle>
                      <p className="text-sm text-emerald-700/80">These are the strengths your team has noticed. Send a celebration to thank them!</p>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {behaviourSummary.positives.map(p => (
                          <div key={p.category} className="flex items-center justify-between bg-white rounded-lg p-3 border border-emerald-100" data-testid={`positive-behaviour-${p.category}`}>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-slate-900 text-sm">{p.name}</p>
                              <p className="text-xs text-slate-500">{p.count} observation{p.count !== 1 ? 's' : ''}</p>
                            </div>
                            <Button
                              size="sm"
                              onClick={() => openCelebrate(p)}
                              className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 text-xs gap-1 shrink-0"
                              data-testid={`celebrate-btn-${p.category}`}
                            >
                              <Sparkles className="h-3.5 w-3.5" /> Celebrate
                            </Button>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Challenges */}
                {behaviourSummary.challenges.length > 0 && (
                  <Card className="border-slate-200">
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2">
                        <Activity className="h-4 w-4 text-amber-500" /> Things To Work On
                      </CardTitle>
                      <p className="text-sm text-slate-500">
                        Areas where your team is supporting you. Talk to your worker or supervisor if you'd like to discuss any of these.
                      </p>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {behaviourSummary.challenges.map(c => {
                          const trendIcon = c.trend === 'improving' ? (
                            <TrendingDown className="h-4 w-4 text-emerald-500" />
                          ) : c.trend === 'worsening' ? (
                            <TrendingUp className="h-4 w-4 text-rose-500" />
                          ) : (
                            <Minus className="h-4 w-4 text-slate-400" />
                          );
                          const sevColor = c.severity_level === 'high' ? 'bg-rose-100 text-rose-700 border-rose-200'
                            : c.severity_level === 'medium' ? 'bg-amber-100 text-amber-700 border-amber-200'
                            : 'bg-slate-100 text-slate-600 border-slate-200';
                          return (
                            <div key={c.category} className="bg-white border border-slate-200 rounded-lg p-3" data-testid={`challenge-behaviour-${c.category}`}>
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <p className="font-medium text-slate-900 text-sm">{c.name}</p>
                                    <Badge variant="outline" className={`text-[10px] capitalize border ${sevColor}`}>
                                      {c.severity_level}
                                    </Badge>
                                  </div>
                                  <p className="text-xs text-slate-500 mt-0.5">
                                    {c.count} observation{c.count !== 1 ? 's' : ''} · Avg intensity {c.avg_severity}/10
                                  </p>
                                  {c.common_triggers.length > 0 && (
                                    <div className="flex items-center gap-1 mt-2 flex-wrap">
                                      <span className="text-[10px] text-slate-400 uppercase tracking-wide">Triggers:</span>
                                      {c.common_triggers.slice(0, 4).map((t, i) => (
                                        <span key={i} className="text-[11px] bg-slate-100 text-slate-600 rounded px-2 py-0.5">{t}</span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                                <div className="flex items-center gap-1.5 shrink-0">
                                  {trendIcon}
                                  <span className={`text-xs capitalize ${c.trend === 'improving' ? 'text-emerald-600' : c.trend === 'worsening' ? 'text-rose-600' : 'text-slate-500'}`}>
                                    {c.trend.replace('_', ' ')}
                                  </span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Month-over-Month */}
                {behaviourComparison && behaviourComparison.comparison.length > 0 && (
                  <Card className="border-slate-200">
                    <CardHeader>
                      <CardTitle className="text-base">
                        {behaviourComparison.current_month} vs {behaviourComparison.previous_month}
                      </CardTitle>
                      <p className="text-sm text-slate-500">How things are changing month to month.</p>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                              <th className="py-2 pr-3">Category</th>
                              <th className="py-2 px-3 text-center">{behaviourComparison.previous_month}</th>
                              <th className="py-2 px-3 text-center">{behaviourComparison.current_month}</th>
                              <th className="py-2 pl-3 text-right">Trend</th>
                            </tr>
                          </thead>
                          <tbody>
                            {behaviourComparison.comparison.map(row => (
                              <tr key={row.category} className="border-b border-slate-100 last:border-0" data-testid={`comparison-row-${row.category}`}>
                                <td className="py-2 pr-3">
                                  <span className="font-medium text-slate-800">{row.name}</span>
                                  {row.is_positive_behaviour && (
                                    <Badge className="ml-2 bg-emerald-50 text-emerald-700 border border-emerald-200 text-[9px]">POSITIVE</Badge>
                                  )}
                                </td>
                                <td className="py-2 px-3 text-center text-slate-600">{row.previous_month.count}</td>
                                <td className="py-2 px-3 text-center text-slate-900 font-medium">{row.current_month.count}</td>
                                <td className="py-2 pl-3 text-right">
                                  <span className={`inline-flex items-center gap-1 text-xs capitalize ${row.trend === 'improving' ? 'text-emerald-600' : row.trend === 'worsening' ? 'text-rose-600' : 'text-slate-500'}`}>
                                    {row.trend === 'improving' ? <TrendingDown className="h-3 w-3" /> : row.trend === 'worsening' ? <TrendingUp className="h-3 w-3" /> : <Minus className="h-3 w-3" />}
                                    {row.trend}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Recent records timeline */}
                {behaviourRecords.length > 0 && (
                  <Card className="border-slate-200">
                    <CardHeader>
                      <CardTitle className="text-base">Recent Observations</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 max-h-96 overflow-y-auto">
                        {behaviourRecords.slice(0, 20).map(r => (
                          <div key={r.id} className="flex items-start gap-3 p-2.5 bg-slate-50 rounded-lg" data-testid={`behaviour-record-${r.id}`}>
                            <div className={`h-2 w-2 rounded-full mt-2 shrink-0 ${r.severity_score >= 7 ? 'bg-rose-500' : r.severity_score >= 4 ? 'bg-amber-500' : 'bg-emerald-500'}`} />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="text-sm font-medium text-slate-800">{r.category_name}</p>
                                <span className="text-xs text-slate-500">{r.report_date}</span>
                                <Badge variant="outline" className="text-[10px]">Intensity {r.severity_score}/10</Badge>
                              </div>
                              {r.trigger && (
                                <p className="text-xs text-slate-500 mt-0.5">Trigger: {r.trigger}</p>
                              )}
                              {r.notes && (
                                <p className="text-xs text-slate-600 mt-0.5">{r.notes}</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </TabsContent>


          {/* =================== REPORTS TAB =================== */}
          <TabsContent value="reports" className="space-y-4">
            {(dashboard?.reports?.length || 0) === 0 ? (
              <Card className="border-slate-200">
                <CardContent className="py-12 text-center">
                  <FileText className="h-10 w-10 mx-auto mb-3 text-slate-300" />
                  <p className="text-slate-400">No approved reports yet</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {dashboard.reports.map(r => (
                  <Card key={r.id} className="border-slate-200 hover:border-teal-300 transition-colors cursor-pointer" data-testid={`report-item-${r.id}`}>
                    <CardContent className="p-4" onClick={async () => {
                      try {
                        const res = await participantApi.getReport(r.id);
                        setSelectedReport({ ...res.data.report, _comments: res.data.comments });
                      } catch { toast.error('Failed to load report'); }
                    }}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-slate-900">{r.template_type?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</p>
                          <p className="text-sm text-slate-500">By {r.worker_name} - {new Date(r.created_at).toLocaleDateString('en-AU', { timeZone: 'Australia/Sydney' })}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {r.compliance_score && (
                            <Badge className={r.compliance_score >= 85 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                              {r.compliance_score}%
                            </Badge>
                          )}
                          <Badge variant="outline" className="capitalize">{r.status}</Badge>
                          <ChevronRight className="h-4 w-4 text-slate-400" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Report Detail Modal */}
            {selectedReport && (
              <Dialog open={!!selectedReport} onOpenChange={() => setSelectedReport(null)}>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>{selectedReport.template_type?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="flex gap-2 text-sm text-slate-500">
                      <span>By {selectedReport.worker_name}</span>
                      <span>-</span>
                      <span>{new Date(selectedReport.created_at).toLocaleDateString('en-AU', { timeZone: 'Australia/Sydney' })}</span>
                    </div>

                    {selectedReport.ai_generated_report && (
                      <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
                        <p className="font-medium text-teal-800 mb-2">AI-Generated Report</p>
                        <p className="text-sm text-teal-700 whitespace-pre-wrap">{selectedReport.ai_generated_report}</p>
                      </div>
                    )}

                    {selectedReport.form_data && Object.keys(selectedReport.form_data).length > 0 && (
                      <div className="space-y-2">
                        <p className="font-medium text-slate-700">Shift Details</p>
                        {Object.entries(selectedReport.form_data).map(([key, val]) => {
                          if (!val || key.startsWith('shift_photos')) return null;
                          const label = key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                          const display = typeof val === 'object' ? JSON.stringify(val) : String(val);
                          return (
                            <div key={key} className="text-sm">
                              <span className="font-medium text-slate-600">{label}: </span>
                              <span className="text-slate-500">{display}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Comments */}
                    <div className="border-t pt-4 space-y-3">
                      <p className="font-medium text-slate-700">Comments & Feedback</p>
                      {(selectedReport._comments || []).map(c => (
                        <div key={c.id} className="bg-slate-50 rounded-lg p-3 text-sm">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-slate-700">{c.user_name}</span>
                            <Badge variant="outline" className="text-[10px]">{c.user_role}</Badge>
                            <span className="text-xs text-slate-400">{new Date(c.created_at).toLocaleString('en-AU', { timeZone: 'Australia/Sydney' })}</span>
                          </div>
                          <p className="text-slate-600">{c.text}</p>
                        </div>
                      ))}
                      <div className="flex gap-2">
                        <Input value={commentText} onChange={(e) => setCommentText(e.target.value)} placeholder="Leave a comment..."
                          className="flex-1" data-testid="report-comment-input" onKeyDown={(e) => e.key === 'Enter' && handleAddComment()} />
                        <Button onClick={handleAddComment} disabled={sendingComment || !commentText.trim()} className="bg-teal-600 hover:bg-teal-700" data-testid="send-comment-btn">
                          <Send className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </TabsContent>

          {/* =================== SHIFTS TAB =================== */}
          <TabsContent value="shifts" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card className="border-slate-200">
                <CardHeader><CardTitle className="text-base flex items-center gap-2"><Calendar className="h-4 w-4" /> Request or Cancel Shift</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label className="text-sm">Request Type</Label>
                    <Select value={shiftType} onValueChange={setShiftType}>
                      <SelectTrigger className="mt-1" data-testid="shift-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="new">Request New Shift</SelectItem>
                        <SelectItem value="cancel">Cancel Existing Shift</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm">Preferred Date</Label>
                    <Input type="date" value={shiftDate} onChange={(e) => setShiftDate(e.target.value)} className="mt-1" data-testid="shift-date-input" />
                  </div>
                  <div>
                    <Label className="text-sm">Preferred Time (optional)</Label>
                    <Input type="time" value={shiftTime} onChange={(e) => setShiftTime(e.target.value)} className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-sm">Notes (optional)</Label>
                    <Textarea value={shiftNotes} onChange={(e) => setShiftNotes(e.target.value)} placeholder="Any details about the shift..." rows={2} className="mt-1" data-testid="shift-notes" />
                  </div>
                  <Button onClick={handleShiftRequest} disabled={savingShift} className="w-full bg-teal-600 hover:bg-teal-700" data-testid="submit-shift-btn">
                    <Calendar className="h-4 w-4 mr-2" /> {savingShift ? 'Submitting...' : shiftType === 'new' ? 'Request Shift' : 'Submit Cancellation'}
                  </Button>
                </CardContent>
              </Card>

              <Card className="border-slate-200">
                <CardHeader><CardTitle className="text-base">Recent Requests</CardTitle></CardHeader>
                <CardContent>
                  {(dashboard?.shift_requests?.length || 0) === 0 ? (
                    <p className="text-slate-400 text-sm text-center py-6">No shift requests yet</p>
                  ) : (
                    <div className="space-y-2">
                      {dashboard.shift_requests.map(r => (
                        <div key={r.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg text-sm" data-testid="shift-request-item">
                          <div>
                            <p className="font-medium text-slate-700 capitalize">{r.request_type === 'new' ? 'New Shift' : 'Cancellation'}</p>
                            <p className="text-xs text-slate-500">{r.preferred_date}{r.preferred_time ? ` at ${r.preferred_time}` : ''}</p>
                          </div>
                          <Badge variant="outline" className={r.status === 'pending' ? 'text-amber-600 border-amber-300' : 'text-emerald-600 border-emerald-300'}>
                            {r.status}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* =================== CONNECT TAB =================== */}
          <TabsContent value="connect" className="space-y-4">
            <Card className="border-slate-200">
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><MessageSquare className="h-4 w-4" /> Request a Meeting</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-sm">I'd like to meet with</Label>
                  <Select value={meetingWith} onValueChange={setMeetingWith}>
                    <SelectTrigger className="mt-1" data-testid="meeting-with-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="worker">My Support Worker</SelectItem>
                      <SelectItem value="supervisor">A Supervisor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm">Preferred Date (optional)</Label>
                  <Input type="date" value={meetingDate} onChange={(e) => setMeetingDate(e.target.value)} className="mt-1" />
                </div>
                <div>
                  <Label className="text-sm">Reason (optional)</Label>
                  <Textarea value={meetingReason} onChange={(e) => setMeetingReason(e.target.value)} placeholder="What would you like to discuss?" rows={3} className="mt-1" data-testid="meeting-reason" />
                </div>
                <Button onClick={handleMeetingRequest} disabled={savingMeeting} className="bg-teal-600 hover:bg-teal-700" data-testid="submit-meeting-btn">
                  <MessageSquare className="h-4 w-4 mr-2" /> {savingMeeting ? 'Sending...' : 'Request Meeting'}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Celebrate Dialog */}
      <Dialog open={celebrateOpen} onOpenChange={setCelebrateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-emerald-500" />
              Celebrate this win!
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
              <p className="text-sm text-emerald-800">
                You're celebrating <strong>{celebrateCategory?.name}</strong> ({celebrateCategory?.count} observation{celebrateCategory?.count !== 1 ? 's' : ''}).
                Your support worker{celebrateCategory?.count !== 1 ? 's' : ''} will get a thank-you notification 💚
              </p>
            </div>
            <div>
              <Label className="text-sm">Personal message (optional)</Label>
              <Textarea
                value={celebrateMessage}
                onChange={(e) => setCelebrateMessage(e.target.value)}
                placeholder="Thanks for helping me make progress in this area!"
                rows={3}
                className="mt-1"
                data-testid="celebrate-message-input"
              />
              <p className="text-xs text-slate-400 mt-1">Leave blank to send a default thank-you.</p>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setCelebrateOpen(false)} disabled={sendingCelebration}>
                Cancel
              </Button>
              <Button
                onClick={handleSendCelebration}
                disabled={sendingCelebration}
                className="bg-emerald-600 hover:bg-emerald-700 gap-2"
                data-testid="send-celebration-btn"
              >
                <Sparkles className="h-4 w-4" />
                {sendingCelebration ? 'Sending...' : 'Send Celebration'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
